class DateField extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector(".date-field__input");

    if (!this.input) return;

    this.input.addEventListener("input", this.onInput.bind(this));
    this.input.addEventListener("blur", this.validate.bind(this));

    this.toggle = this.querySelector(".date-field__icon");
    this.calendar = this.querySelector(".date-field__calendar");

    if (this.toggle && this.calendar) {
      this.initCalendar();
    }
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.handleDocumentClick);
    document.removeEventListener("keydown", this.handleCalendarKeyDown);
  }

  onInput() {
    this.input.value = this.formatDate(this.input.value);
    this.clearError();
  }

  formatDate(value) {
    const digits = value.replace(/\D/g, "").slice(0, 8);
    const parts = [];

    if (digits.length > 0) parts.push(digits.slice(0, 2));
    if (digits.length > 2) parts.push(digits.slice(2, 4));
    if (digits.length > 4) parts.push(digits.slice(4, 8));

    return parts.join("/");
  }

  isValidDate(value) {
    const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

    if (!match) return false;

    const month = Number(match[1]);
    const day = Number(match[2]);
    const year = Number(match[3]);

    const date = new Date(year, month - 1, day);

    return (
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day
    );
  }

  validate() {
    if (!this.input.value) {
      this.clearError();
      return;
    }

    const hasError = !this.isValidDate(this.input.value);

    this.classList.toggle("date-field--error", hasError);
    this.input.classList.toggle("error", hasError);
  }

  clearError() {
    this.classList.remove("date-field--error");
    this.input.classList.remove("error");
  }

  // Calendar dropdown

  initCalendar() {
    this.calendarTitle = this.calendar.querySelector(".date-field__calendar-title");
    this.weekdays = this.calendar.querySelector(".date-field__calendar-weekdays");
    this.days = this.calendar.querySelector(".date-field__calendar-days");
    this.prevButton = this.calendar.querySelector(".date-field__calendar-nav--prev");
    this.nextButton = this.calendar.querySelector(".date-field__calendar-nav--next");

    const locale = document.documentElement.lang || undefined;

    this.monthFormatter = new Intl.DateTimeFormat(locale, {
      month: "long",
      year: "numeric",
    });

    this.weekdayFormatter = new Intl.DateTimeFormat(locale, { weekday: "short" });

    this.today = this.startOfDay(new Date());
    this.viewDate = this.today;

    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleCalendarKeyDown = this.handleCalendarKeyDown.bind(this);

    this.renderWeekdays();

    // Keep focus on the input while interacting with the calendar so a day
    // click doesn't blur/validate against the pre-selection value first.
    this.calendar.addEventListener("mousedown", (event) => event.preventDefault());

    this.input.addEventListener("click", () => this.open());

    this.toggle.addEventListener("click", (event) => {
      event.preventDefault();
      this.isOpen ? this.close() : this.open();
    });

    this.prevButton.addEventListener("click", () => this.changeMonth(-1));
    this.nextButton.addEventListener("click", () => this.changeMonth(1));

    this.days.addEventListener("click", (event) => {
      const button = event.target.closest("button[data-date]");

      if (!button || button.disabled) return;

      this.selectDate(this.parseISODate(button.dataset.date));
    });
  }

  // Derived from the DOM rather than tracked in a separate flag: the cart
  // section can be morphed (re-rendered) by an unrelated field's autosave at
  // any time, which resets `hidden` back to the server-rendered markup. A
  // separately-tracked boolean would go stale when that happens and leave
  // open() silently no-op'ing on the next click.
  get isOpen() {
    return !!this.calendar && !this.calendar.hidden;
  }

  open() {
    if (!this.calendar || this.isOpen) return;

    this.calendar.hidden = false;
    this.classList.add("date-field--open");
    this.toggle.setAttribute("aria-expanded", "true");

    this.viewDate = this.parseInputDate() || this.today;
    this.renderWeekdays();
    this.renderDays();

    document.addEventListener("click", this.handleDocumentClick);
    document.addEventListener("keydown", this.handleCalendarKeyDown);
  }

  close() {
    if (!this.isOpen) return;

    this.calendar.hidden = true;
    this.classList.remove("date-field--open");
    this.toggle.setAttribute("aria-expanded", "false");

    document.removeEventListener("click", this.handleDocumentClick);
    document.removeEventListener("keydown", this.handleCalendarKeyDown);
  }

  setDisabled(disabled) {
    this.input.disabled = disabled;
    if (this.toggle) this.toggle.disabled = disabled;
    if (disabled) this.close();
  }

  handleDocumentClick(event) {
    if (!this.contains(event.target)) this.close();
  }

  handleCalendarKeyDown(event) {
    if (event.key === "Escape") {
      this.close();
      this.input.focus();
      return;
    }

    const button = event.target.closest("button[data-date]");

    if (!button) return;

    const date = this.parseISODate(button.dataset.date);

    if (event.key === "ArrowLeft") {
      event.preventDefault();
      date.setDate(date.getDate() - 1);
      this.moveFocusTo(date);
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      date.setDate(date.getDate() + 1);
      this.moveFocusTo(date);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      date.setDate(date.getDate() - 7);
      this.moveFocusTo(date);
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      date.setDate(date.getDate() + 7);
      this.moveFocusTo(date);
    } else if (event.key === "Home") {
      event.preventDefault();
      date.setDate(date.getDate() - date.getDay());
      this.moveFocusTo(date);
    } else if (event.key === "End") {
      event.preventDefault();
      date.setDate(date.getDate() + (6 - date.getDay()));
      this.moveFocusTo(date);
    }
  }

  moveFocusTo(date) {
    if (date < this.today) return;

    if (
      date.getMonth() !== this.viewDate.getMonth() ||
      date.getFullYear() !== this.viewDate.getFullYear()
    ) {
      this.viewDate = this.startOfMonth(date);
      this.renderDays();
    }

    this.days.querySelector(`button[data-date="${this.formatISODate(date)}"]`)?.focus();
  }

  changeMonth(delta) {
    const next = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() + delta, 1);

    if (this.endOfMonth(next) < this.today) return;

    this.viewDate = next;
    this.renderDays();
  }

  selectDate(date) {
    this.input.value = this.formatDisplayDate(date);
    this.input.dispatchEvent(new Event("input", { bubbles: true }));
    this.input.dispatchEvent(new Event("blur", { bubbles: true }));

    this.close();
    this.input.focus();
  }

  renderWeekdays() {
    this.weekdays.innerHTML = "";

    // 2023-01-01 is a Sunday; used purely as a stable anchor to format
    // each weekday name in the current locale.
    for (let i = 0; i < 7; i++) {
      const date = new Date(2023, 0, 1 + i);
      const cell = document.createElement("span");
      cell.className = "date-field__calendar-weekday";
      cell.textContent = this.weekdayFormatter.format(date);
      this.weekdays.appendChild(cell);
    }
  }

  createMutedDay(day) {
    const cell = document.createElement("span");
    cell.className = "date-field__calendar-day date-field__calendar-day--muted";
    cell.textContent = String(day);
    return cell;
  }

  renderDays() {
    const year = this.viewDate.getFullYear();
    const month = this.viewDate.getMonth();
    const isCurrentMonth = year === this.today.getFullYear() && month === this.today.getMonth();

    this.calendarTitle.textContent = this.monthFormatter.format(this.viewDate);
    this.prevButton.disabled = isCurrentMonth;
    this.prevButton.classList.toggle("date-field__calendar-nav--disabled", isCurrentMonth);

    this.days.innerHTML = "";

    const firstWeekday = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const prevMonthDays = new Date(year, month, 0).getDate();
    const selected = this.parseInputDate();

    for (let i = firstWeekday - 1; i >= 0; i--) {
      this.days.appendChild(this.createMutedDay(prevMonthDays - i));
    }

    let focusableSet = false;

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const isPast = date < this.today;
      const isToday = this.isSameDay(date, this.today);
      const isSelected = selected && this.isSameDay(date, selected);

      const button = document.createElement("button");
      button.type = "button";
      button.className = "date-field__calendar-day";
      button.textContent = String(day);
      button.dataset.date = this.formatISODate(date);

      if (isPast) {
        button.disabled = true;
        button.classList.add("date-field__calendar-day--disabled");
      }

      if (isToday) button.classList.add("date-field__calendar-day--today");

      if (isSelected) {
        button.classList.add("date-field__calendar-day--selected");
        button.setAttribute("aria-selected", "true");
      }

      if (!isPast && (isSelected || (!focusableSet && (isToday || !selected)))) {
        button.tabIndex = 0;
        focusableSet = true;
      } else {
        button.tabIndex = -1;
      }

      this.days.appendChild(button);
    }

    const totalCells = firstWeekday + daysInMonth;
    const trailing = (7 - (totalCells % 7)) % 7;

    for (let day = 1; day <= trailing; day++) {
      this.days.appendChild(this.createMutedDay(day));
    }

    if (!focusableSet) {
      const firstEnabled = this.days.querySelector("button:not([disabled])");
      if (firstEnabled) firstEnabled.tabIndex = 0;
    }
  }

  parseInputDate() {
    if (!this.isValidDate(this.input.value)) return null;

    const [month, day, year] = this.input.value.split("/").map(Number);

    return new Date(year, month - 1, day);
  }

  parseISODate(value) {
    const [year, month, day] = value.split("-").map(Number);

    return new Date(year, month - 1, day);
  }

  formatISODate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }

  formatDisplayDate(date) {
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${month}/${day}/${date.getFullYear()}`;
  }

  startOfDay(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  startOfMonth(date) {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }

  endOfMonth(date) {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
  }

  isSameDay(a, b) {
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }
}

if (!customElements.get("date-field")) {
  customElements.define("date-field", DateField);
}
